import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Trophy, TrendingUp, Music, Play, Heart, ChevronRight,
  ArrowUp, ArrowDown, Minus, Loader2, ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

/**
 * Creator Chart Analytics - Dashboard view with trends and comparisons
 */
export function CreatorChartAnalytics({ artistHandle }) {
  const { token } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (artistHandle && token) {
      fetchAnalytics();
    }
  }, [artistHandle, token]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API}/artists/${artistHandle}/chart/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAnalyticsData(data);
      } else if (res.status === 403) {
        setError('Access denied');
      } else {
        setError('Failed to load analytics');
      }
    } catch (err) {
      console.error('Failed to fetch chart analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-green-500';
    if (trend === 'down') return 'text-red-500';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <Card className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading font-bold">Your Chart Analytics</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading font-bold">Your Chart Analytics</h3>
        </div>
        <div className="text-center py-6 text-muted-foreground">
          <p className="text-sm">{error}</p>
        </div>
      </Card>
    );
  }

  if (!analyticsData || analyticsData.chart?.length === 0) {
    return (
      <Card className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading font-bold">Your Chart Analytics</h3>
        </div>
        <div className="text-center py-6 text-muted-foreground">
          <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Upload tracks to see your chart analytics</p>
        </div>
      </Card>
    );
  }

  const summary = analyticsData.summary || {};
  const chart = analyticsData.chart || [];

  return (
    <Card className="glass-card p-6" data-testid="creator-chart-analytics">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading font-bold">Your Chart Analytics</h3>
        </div>
        <Link to={`/charts/creator/${artistHandle}`}>
          <Button variant="ghost" size="sm" className="text-primary">
            View Public Chart
            <ExternalLink className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-primary">{summary.total_tracks || 0}</p>
          <p className="text-xs text-muted-foreground">Total Tracks</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-secondary">{summary.total_requests_this_week || 0}</p>
          <p className="text-xs text-muted-foreground">Requests (This Week)</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1">
            <span className={`text-2xl font-bold ${getTrendColor(summary.trend)}`}>
              {summary.change > 0 ? '+' : ''}{summary.change || 0}
            </span>
            {getTrendIcon(summary.trend)}
          </div>
          <p className="text-xs text-muted-foreground">vs Last Week</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold">{summary.total_requests_last_week || 0}</p>
          <p className="text-xs text-muted-foreground">Last Week</p>
        </div>
      </div>

      {/* Top Tracks with Trends */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Top Performers</h4>
        {chart.slice(0, 5).map((track, index) => (
          <motion.div
            key={track.track_id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <div className={`flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors ${
              track.position <= 3 ? 'border border-primary/20' : ''
            }`}>
              {/* Position */}
              <div className="w-8 text-center flex-shrink-0">
                {track.badge ? (
                  <span className="text-lg">{track.badge}</span>
                ) : (
                  <span className="text-sm font-bold text-muted-foreground">
                    {track.position}
                  </span>
                )}
              </div>

              {/* Track Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{track.title}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {track.current_week?.requests || 0} requests
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-3 h-3" />
                    {track.current_week?.likes || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Play className="w-3 h-3" />
                    {(track.current_week?.plays || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Trend */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`flex items-center gap-1 ${getTrendColor(track.change?.trend)}`}>
                  {getTrendIcon(track.change?.trend)}
                  <span className="text-sm font-medium">
                    {track.change?.requests > 0 ? '+' : ''}{track.change?.requests || 0}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Scoring Info */}
      <div className="mt-4 pt-3 border-t border-border text-xs text-muted-foreground text-center">
        Score = (Requests × 3) + (Likes × 2) + (Plays × 1)
      </div>
    </Card>
  );
}

export default CreatorChartAnalytics;
